// The per-install device id. Separate from clock.ts on purpose: this is
// the one module in progress/ that touches localStorage, and keeping it here
// is what lets clock.ts, event.ts, reduce.ts, fold.ts and query.ts stay
// runnable outside a browser.

import type { DeviceId } from "@aybuben/core/progress/clock.ts";
import { uid } from "@aybuben/core/rng.ts";

/** Per browser install; never cleared, not even on sign-out. */
export const DEVICE_KEY = "aybuben:device";

// Memoized because a blocked-storage browser would otherwise mint a fresh id
// on every call, and `order`'s device tiebreak would then vary within a single
// session. globalThis.localStorage, not window: this module is tested with no DOM.
let cached: DeviceId | null = null;

export function deviceId(): DeviceId {
	if (cached) return cached;
	try {
		const stored = globalThis.localStorage?.getItem(DEVICE_KEY);
		if (stored) {
			cached = stored;
			return stored;
		}
	} catch {
		// Storage unavailable; fall through and mint an in-memory id.
	}
	const minted = uid();
	try {
		globalThis.localStorage?.setItem(DEVICE_KEY, minted);
	} catch {
		// Storage unavailable; the id lives for this session only.
	}
	cached = minted;
	return minted;
}

/** Test seam only: forget the memo so a test can install a different store. */
export const forgetDevice = (): void => {
	cached = null;
};
